import { useStore } from "../state/store";
import { ageText, sortSystems, STALE_FIX_H, systemName } from "../engine/liveSystems";
import { buildSystemPins } from "../map/systemLayers";

const BASIN_LABEL: Record<string, string> = { AL: "Atlantic", EP: "East Pacific", CP: "Central Pacific" };

/**
 * The live view's landing panel: every active storm and invest, the same
 * systems the map shows as symbols. Clicking one (here or on the map) opens
 * its forecast, guidance and history.
 */
export default function ActiveSystems() {
  const index = useStore((s) => s.live.index);
  const indexKind = useStore((s) => s.live.indexKind);
  const loading = useStore((s) => s.live.loading);
  const gtwo = useStore((s) => s.live.genesis?.gtwo);
  const selectLiveStorm = useStore((s) => s.selectLiveStorm);

  const nhcLink = (
    <div className="muted xs center" style={{ marginTop: 14 }}>
      For official watches, warnings & advisories:{" "}
      <a href="https://www.nhc.noaa.gov" target="_blank" rel="noreferrer" style={{ color: "#60A5FA" }}>nhc.noaa.gov ↗</a>
    </div>
  );

  if (indexKind !== "active") {
    return (
      <>
        <div className="muted xs">🌀 ACTIVE SYSTEMS</div>
        <p style={{ margin: "8px 0 4px", fontSize: 13 }}>
          {loading ? "Checking NHC for active systems…" : "No active tropical cyclones right now."}
        </p>
        {!loading && (
          <p className="muted xs" style={{ textTransform: "none", letterSpacing: 0, lineHeight: 1.5 }}>
            Shaded areas on the map are NHC's outlook for where one could form. Or replay a past
            storm as it looked live:
          </p>
        )}
        <div className="sys-list">
          {index.map((s) => (
            <button key={s.storm_id} className="sys-card plain" onClick={() => void selectLiveStorm(s.storm_id)}>
              <span className="sys-name">{s.name ?? s.storm_id}{s.year ? ` (${s.year})` : ""}</span>
            </button>
          ))}
        </div>
        {nhcLink}
      </>
    );
  }

  const sorted = sortSystems(index);
  const pins = new Map(buildSystemPins(sorted, gtwo).pins.map((p) => [p.id, p]));
  const basins = [...new Set(sorted.map((s) => (s.basin ?? "AL").toUpperCase()))];

  return (
    <>
      <div className="muted xs">🌀 ACTIVE SYSTEMS · {index.length}</div>
      <p className="muted xs" style={{ marginTop: 4, textTransform: "none", letterSpacing: 0 }}>
        Click a storm on the map or below for its forecast, model guidance and history.
      </p>
      {basins.map((b) => (
        <div key={b}>
          <div className="muted xs" style={{ marginTop: 12 }}>{BASIN_LABEL[b] ?? b}</div>
          <div className="sys-list">
            {sorted.filter((s) => (s.basin ?? "AL").toUpperCase() === b).map((s) => {
              const p = pins.get(s.storm_id);
              if (!p) {
                return (
                  <button key={s.storm_id} className="sys-card plain" onClick={() => void selectLiveStorm(s.storm_id)}>
                    <span className="sys-name">{systemName(s)}</span>
                  </button>
                );
              }
              const stale = p.ageH != null && p.ageH > STALE_FIX_H;
              const ri = p.dv24 != null && p.dv24 >= 30;
              return (
                <button
                  key={s.storm_id} className={stale ? "sys-card stale" : "sys-card"}
                  style={{ borderLeftColor: p.color }} onClick={() => void selectLiveStorm(s.storm_id)}
                >
                  <img src={p.icon} alt="" />
                  <span>
                    <span className="sys-top">
                      <span className="sys-name">{p.name}</span>
                      <span className="sys-class" style={{ color: p.color }}>
                        {p.kind === "invest"
                          ? p.odds?.prob7 != null ? `7-day ${p.odds.prob7}%` : "Invest"
                          : p.kind === "potential" ? "Potential TC"
                            : p.classLabel.replace(/^Major Hurricane · /, "").replace(/^Hurricane · /, "")}
                      </span>
                    </span>
                    <span className="sys-line" style={{ display: "block" }}>
                      {p.vmax != null ? `${p.vmax} kt` : "—"}
                      {p.mslp != null && ` · ${p.mslp} mb`}
                      {p.dv24 ? (
                        <b style={{ color: p.dv24 > 0 ? "#F87171" : "#4ADE80" }}>
                          {" "}{p.dv24 > 0 ? "▲" : "▼"}{Math.abs(p.dv24)} kt/24 h{ri && " · RI"}
                        </b>
                      ) : null}
                    </span>
                    <span className="sys-line" style={{ display: "block" }}>
                      {p.motion ?? "Motion n/a"}
                      {p.ageH != null && (
                        <span style={stale ? { color: "#FBBF24" } : undefined}> · fix {ageText(p.ageH)} ago</span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {nhcLink}
    </>
  );
}
